import { monthName, monthOf, shortDateLabel } from "./format";
import { EXPECTATION_MEASURE_LABELS, formatExpectationRange } from "./marketIntelligenceViews";
import { ANNOUNCEMENT_STATUS_LABELS, nominationAmountLabel, type NominationCard } from "./nominationBoard";
import { formatUsdPerLb } from "./callForm";
import type { Headline, LogRequest, Note, PricePoint, PublisherView } from "./types";

export interface DrawerField {
  label: string;
  value: string;
}

export interface SourceDrawerContent {
  kindLabel: string;
  title: string;
  fields: DrawerField[];
  sourceReference: string | null;
  sourceUrl: string | null;
  enteredByDisplayName: string | null;
  recordedUtc: string | null;
  actionLabel: string;
  logRequest: LogRequest;
}

interface CodedRecord {
  designationCode?: string | null;
  baseCode?: string | null;
  additiveCode?: string | null;
  colorCode?: string | null;
}

function presentFields(labelledValues: [string, string | null | undefined][]): DrawerField[] {
  return labelledValues
    .filter((labelledValue): labelledValue is [string, string] => Boolean(labelledValue[1]?.trim()))
    .map(([label, value]) => ({ label, value }));
}

function codeFields(record: CodedRecord): [string, string | null | undefined][] {
  return [
    ["Resin code", record.designationCode],
    ["Base code", record.baseCode],
    ["Additive code", record.additiveCode],
    ["Color code", record.colorCode],
  ];
}

export function nominationDrawerContent(card: NominationCard, month: string): SourceDrawerContent {
  const nomination = card.latestNomination;
  const title = `${card.supplierName} · ${monthName(month)}`;
  if (!nomination) {
    return {
      kindLabel: "Nomination",
      title,
      fields: presentFields([["Status", ANNOUNCEMENT_STATUS_LABELS.NotYetChecked]]),
      sourceReference: null,
      sourceUrl: null,
      enteredByDisplayName: null,
      recordedUtc: null,
      actionLabel: "Log Nomination",
      logRequest: {
        kind: "Nomination",
        recordId: null,
        recordMonth: `${month}-01`,
        presetValues: { supplierId: String(card.supplierId) },
      },
    };
  }
  const amountCentsPerLb = nomination.amountCentsPerLb;
  return {
    kindLabel: "Nomination",
    title,
    fields: presentFields([
      ["Status", ANNOUNCEMENT_STATUS_LABELS[nomination.announcementStatusCode]],
      [
        "Change",
        amountCentsPerLb === null || amountCentsPerLb === undefined ? null : nominationAmountLabel(amountCentsPerLb),
      ],
      ["Effective month", monthName(monthOf(nomination.effectiveMonth))],
      ["Checked", shortDateLabel(nomination.checkDate)],
      ...codeFields(nomination),
      ["Notes", nomination.notes],
    ]),
    sourceReference: nomination.sourceReference,
    sourceUrl: nomination.sourceUrl ?? null,
    enteredByDisplayName: nomination.enteredByDisplayName ?? null,
    recordedUtc: nomination.recordedUtc ?? null,
    actionLabel: "Edit Nomination",
    logRequest: {
      kind: "Nomination",
      recordId: nomination.cockpitNominationId ?? null,
      recordMonth: nomination.effectiveMonth,
    },
  };
}

export function headlineDrawerContent(headline: Headline): SourceDrawerContent {
  return {
    kindLabel: "Headline",
    title: headline.title,
    fields: presentFields([
      ["Publisher", headline.publisherName],
      ["Published", shortDateLabel(headline.publishedDate)],
      ["Region", headline.regionCode],
      ...codeFields(headline),
      ["Notes", headline.notes],
    ]),
    sourceReference: headline.publisherName,
    sourceUrl: headline.sourceUrl ?? null,
    enteredByDisplayName: headline.enteredByDisplayName ?? null,
    recordedUtc: headline.recordedUtc ?? null,
    actionLabel: "Edit Headline",
    logRequest: {
      kind: "Headline",
      recordId: headline.marketHeadlineId ?? null,
      recordMonth: `${monthOf(headline.publishedDate)}-01`,
    },
  };
}

export function expectationDrawerContent(expectation: PublisherView): SourceDrawerContent {
  return {
    kindLabel: "Publisher expectation",
    title: `${expectation.publisherName} · ${EXPECTATION_MEASURE_LABELS[expectation.measureCode]}`,
    fields: presentFields([
      ["Range", formatExpectationRange(expectation.measureCode, expectation.lowValue, expectation.highValue)],
      ["Forecast month", monthName(monthOf(expectation.forecastMonth))],
      ["As of", shortDateLabel(expectation.asOfDate)],
      ["Basis", expectation.basisText],
      ["Region", expectation.regionCode],
      ...codeFields(expectation),
      ["Notes", expectation.notes],
    ]),
    sourceReference: expectation.sourceReference,
    sourceUrl: expectation.sourceUrl ?? null,
    enteredByDisplayName: expectation.enteredByDisplayName ?? null,
    recordedUtc: expectation.recordedUtc ?? null,
    actionLabel: "Edit Expectation",
    logRequest: {
      kind: "Forecast",
      recordId: expectation.publisherForecastId ?? null,
      recordMonth: expectation.forecastMonth,
    },
  };
}

export function pricePointDrawerContent(pricePoint: PricePoint): SourceDrawerContent {
  return {
    kindLabel: "Price point",
    title: `${formatUsdPerLb(pricePoint.priceUsdPerLb)} · ${pricePoint.location}`,
    fields: presentFields([
      ["Price", formatUsdPerLb(pricePoint.priceUsdPerLb)],
      ["Heard", shortDateLabel(pricePoint.heardDate)],
      ["Price type", pricePoint.priceTypeCode],
      ["Basis", pricePoint.priceBasisCode],
      ["Location", pricePoint.location],
      ["Grade confirmation", pricePoint.gradeConfirmationCode],
      ...codeFields(pricePoint),
      ["Notes", pricePoint.notes],
    ]),
    sourceReference: pricePoint.sourceReference,
    sourceUrl: null,
    enteredByDisplayName: pricePoint.enteredByDisplayName ?? null,
    recordedUtc: pricePoint.recordedUtc ?? null,
    actionLabel: "Edit Price Point",
    logRequest: {
      kind: "PricePoint",
      recordId: pricePoint.cockpitPricePointId ?? null,
      recordMonth: `${monthOf(pricePoint.heardDate)}-01`,
    },
  };
}

export function noteDrawerContent(note: Note): SourceDrawerContent {
  return {
    kindLabel: "Consultant note",
    title: `Note · ${monthName(monthOf(note.noteMonth))}`,
    fields: presentFields([["Note", note.body], ...codeFields(note)]),
    sourceReference: null,
    sourceUrl: null,
    enteredByDisplayName: note.enteredByDisplayName ?? null,
    recordedUtc: note.recordedUtc ?? null,
    actionLabel: "Edit Note",
    logRequest: {
      kind: "Note",
      recordId: note.consultantNoteId ?? null,
      recordMonth: note.noteMonth,
    },
  };
}
